import { useState } from 'react';

export type VectorizeSaveDestination = 'local' | 'fias';

interface VectorizeSaveDialogProps {
  /** Default filename (no extension) — usually the source document's name. */
  defaultFileName: string;
  /** Pre-selected destination — same shape as ExportDialog so the editor's
   *  "last picked" preference can carry over between dialogs. */
  defaultDestination?: VectorizeSaveDestination;
  onClose: () => void;
  onSave: (fileName: string, destination: VectorizeSaveDestination) => Promise<void> | void;
}

/**
 * Save-as dialog for the Vectorize tool. Trimmed-down sibling of
 * `ExportDialog` — vectorize is always SVG, full-size, no quality
 * tuning; only the filename and destination need user input.
 *
 * Mirrors `ExportDialog`'s look + Local/Fias destination semantics so
 * users hitting "Vectorize" in the AI panel feel at home if they've
 * used the toolbar's Save As. Resembles the existing component
 * deliberately rather than reusing it because the export hook
 * pipeline (format/quality/dimensions/Sharp processing) doesn't apply
 * to SVG output and shoehorning vector-as-export into that flow
 * would muddy a clean raster pipeline.
 */
export function VectorizeSaveDialog({
  defaultFileName,
  defaultDestination = 'local',
  onClose,
  onSave,
}: VectorizeSaveDialogProps) {
  const [fileName, setFileName] = useState(defaultFileName);
  const [destination, setDestination] = useState<VectorizeSaveDestination>(defaultDestination);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(fileName, destination);
    } finally {
      setIsSaving(false);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
        <h2 className="text-lg font-semibold">Save Vector As&hellip;</h2>

        {/* File name. The dialog appends `.svg` itself (matching the
            ExportDialog's image-extension behavior) — users rarely
            want to type the extension and it surprises them when they
            do. The preview suffix below the field signals it. */}
        <label className="block space-y-1">
          <span className="text-sm font-medium">File name</span>
          <input
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md"
          />
          <span className="text-xs text-muted-foreground block">
            Saved as <span className="font-mono">{stripSvgExtension(fileName) || 'image'}.svg</span>
          </span>
        </label>

        {/* Format — informational only. The vectorize endpoint outputs
            SVG and there is no other choice; surfacing it as a static
            row reassures the user that the file format is locked. */}
        <div className="space-y-1">
          <span className="text-sm font-medium">Format</span>
          <p className="text-xs text-muted-foreground">SVG (scalable vector)</p>
        </div>

        {/* Destination toggle — same component shape as ExportDialog. */}
        <div className="space-y-2">
          <span className="text-sm font-medium">Save to</span>
          <div className="flex gap-2">
            <button
              type="button"
              className={`flex-1 py-2 text-sm rounded-md border ${
                destination === 'local'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
              onClick={() => setDestination('local')}
            >
              Local download
            </button>
            <button
              type="button"
              className={`flex-1 py-2 text-sm rounded-md border ${
                destination === 'fias'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
              onClick={() => setDestination('fias')}
            >
              Fias files
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-md border border-border text-muted-foreground hover:bg-accent"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            onClick={handleSave}
            disabled={isSaving || fileName.trim().length === 0}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function stripSvgExtension(name: string): string {
  return name.replace(/\.svg$/i, '');
}
